import {CollapsibleNavList} from '@github-ui/layout-primitives/CollapsibleNavList'
import {useCollapsibleSidebarContext} from '@github-ui/layout-primitives/CollapsibleSidebar'
import {useQuery} from '@github-ui/react-query'
import {SoftNavOnlyLink as Link, useLocation, useNavigate} from '@github-ui/react-router'
import {getIconColorClassName, getIconComponent, type SavedView} from '@github-ui/saved-views'
import {savedViewsQueryOptions} from '@github-ui/saved-views/queries'
import {PlusIcon, StackIcon} from '@primer/octicons-react'
import {ActionList, NavList} from '@primer/react'

const ALL_VIEWS_LABEL = 'All views'
const DEFAULT_VIEW_NAME = 'Untitled view'
const NEW_VIEW_LABEL = 'New view'
const VIEWS_LABEL = 'Views'

export type RepoSavedViewsNavProps = {
  owner: string
  repo: string
  viewsPath: string
  isCurrent: boolean
  forceHardNavigation: boolean
  onClick?: () => void
}

export function RepoSavedViewsNav({
  owner,
  repo,
  viewsPath,
  isCurrent,
  forceHardNavigation,
  onClick,
}: RepoSavedViewsNavProps) {
  const {isExpanded} = useCollapsibleSidebarContext()

  if (isExpanded) {
    return (
      <CollapsibleNavList.LinkItem
        to={viewsPath}
        icon={StackIcon}
        label={VIEWS_LABEL}
        forceHardNavigation={forceHardNavigation}
        aria-current={isCurrent ? 'page' : undefined}
        onClick={onClick}
      />
    )
  }

  return (
    <CollapsedSavedViewsGroup
      owner={owner}
      repo={repo}
      viewsPath={viewsPath}
      isCurrent={isCurrent}
      forceHardNavigation={forceHardNavigation}
      onClick={onClick}
    />
  )
}

function CollapsedSavedViewsGroup({
  owner,
  repo,
  viewsPath,
  isCurrent,
  forceHardNavigation,
  onClick,
}: RepoSavedViewsNavProps) {
  const navigate = useNavigate()
  const {pathname} = useLocation()
  const {data} = useQuery(savedViewsQueryOptions({owner, repo}))
  const savedViews = data?.savedViews ?? []
  const viewerCanManageViews = data?.viewerCanManageViews ?? false
  const newViewUrl = `${viewsPath}/new`
  const currentViewId = savedViews.find(view => pathname === `${viewsPath}/${encodeURIComponent(view.id)}`)?.id
  const isAllViewsCurrent = isCurrent && currentViewId === undefined

  return (
    <CollapsibleNavList.Group
      icon={StackIcon}
      title={VIEWS_LABEL}
      trailingAction={
        viewerCanManageViews ? (
          <ActionList.GroupHeading.TrailingAction
            as="button"
            icon={PlusIcon}
            label={NEW_VIEW_LABEL}
            onClick={() => {
              if (forceHardNavigation) {
                window.location.assign(newViewUrl)
              } else {
                navigate(newViewUrl)
              }
            }}
            tooltipDirection="e"
          />
        ) : undefined
      }
    >
      {savedViews.map(view => (
        <SavedViewNavItem
          key={view.id}
          view={view}
          viewsPath={viewsPath}
          aria-current={view.id === currentViewId ? 'page' : undefined}
          forceHardNavigation={forceHardNavigation}
          onClick={onClick}
        />
      ))}
      <AllViewsNavItem
        viewsPath={viewsPath}
        aria-current={isAllViewsCurrent ? 'page' : undefined}
        forceHardNavigation={forceHardNavigation}
        onClick={onClick}
      />
    </CollapsibleNavList.Group>
  )
}

function SavedViewNavItem({
  view,
  viewsPath,
  'aria-current': ariaCurrent,
  forceHardNavigation,
  onClick,
}: {
  view: SavedView
  viewsPath: string
  'aria-current'?: 'page'
  forceHardNavigation: boolean
  onClick?: () => void
}) {
  const to = `${viewsPath}/${encodeURIComponent(view.id)}`
  const name = view.name.trim() || DEFAULT_VIEW_NAME

  if (forceHardNavigation) {
    return (
      <NavList.Item href={to} aria-current={ariaCurrent} onClick={onClick}>
        <NavList.LeadingVisual>
          <SavedViewIcon icon={view.icon} color={view.color} />
        </NavList.LeadingVisual>
        {name}
      </NavList.Item>
    )
  }

  return (
    <NavList.Item as={Link} to={to} aria-current={ariaCurrent} onClick={onClick}>
      <NavList.LeadingVisual>
        <SavedViewIcon icon={view.icon} color={view.color} />
      </NavList.LeadingVisual>
      {name}
    </NavList.Item>
  )
}

function AllViewsNavItem({
  viewsPath,
  'aria-current': ariaCurrent,
  forceHardNavigation,
  onClick,
}: {
  viewsPath: string
  'aria-current'?: 'page'
  forceHardNavigation: boolean
  onClick?: () => void
}) {
  if (forceHardNavigation) {
    return (
      <NavList.Item href={viewsPath} aria-current={ariaCurrent} onClick={onClick}>
        <NavList.LeadingVisual>
          <StackIcon />
        </NavList.LeadingVisual>
        {ALL_VIEWS_LABEL}
      </NavList.Item>
    )
  }

  return (
    <NavList.Item as={Link} to={viewsPath} aria-current={ariaCurrent} onClick={onClick}>
      <NavList.LeadingVisual>
        <StackIcon />
      </NavList.LeadingVisual>
      {ALL_VIEWS_LABEL}
    </NavList.Item>
  )
}

function SavedViewIcon({icon, color}: {icon: string; color: string}) {
  const IconComponent = getIconComponent(icon)
  return (
    <span className={getIconColorClassName(color)}>
      {/* eslint-disable-next-line react-hooks/static-components -- getIconComponent returns an imported octicon component. */}
      <IconComponent />
    </span>
  )
}
